import { z } from 'zod';

/**
 * The only module that reads `import.meta.env`.
 *
 * Reading environment variables in one validated place means a typo fails
 * immediately with a clear message, instead of surfacing as an `undefined` at
 * some unrelated point later.
 */
const envSchema = z.object({
  /**
   * Origin the browser sends API requests to. Empty means "this same origin",
   * which in development is handled by the Vite dev server's proxy.
   *
   * It must NOT include `/activist-arc`: the generated client already includes
   * that prefix in every path, so adding it here would produce
   * `/activist-arc/activist-arc/...`.
   */
  VITE_API_BASE_URL: z.string().default(''),

  /** SurveyJS Creator license. Absent means trial mode with a watermark. */
  VITE_SURVEYJS_LICENSE_KEY: z.string().optional(),

  /**
   * Set by Vite: 'development' under the dev server; 'develop', 'staging' or
   * 'production' in a build; or 'test' under Vitest.
   */
  MODE: z.string().default('production'),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  throw new Error(
    `Invalid environment configuration. Compare your .env.local, .env.develop, .env.staging or .env.production file with its .example template.\n${z.prettifyError(parsed.error)}`,
  );
}

export const env = Object.freeze({
  apiBaseUrl: parsed.data.VITE_API_BASE_URL.replace(/\/$/, ''),
  surveyJsLicenseKey: parsed.data.VITE_SURVEYJS_LICENSE_KEY,
  /** True under Vitest, where a pause put in for people to see only slows the run. */
  isTest: parsed.data.MODE === 'test',
});
